import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getMediaResistances } from '../../api/MediaResistanceAPI';
import { AppThunk } from '../../app/store';
import { MediaResistanceDTO } from '../../dto/MediaResistanceDTO';
import { PageDTO } from '../../dto/PageDTO';

interface MediaResistanceState {
  value: MediaResistanceDTO[];
  filter: string;
  isLoading: boolean;
  error: string | null;
  nextPage: string;
  previousPage: string;
  lastPage: string;
  firstPage: string;
  getNextPage: boolean;
  getPrevPage: boolean;
  totalElements: number;
  completeValues: MediaResistanceDTO[];
}

const initialState: MediaResistanceState = {
  value: [],
  filter: '',
  isLoading: true,
  error: null,
  nextPage: '',
  previousPage: '',
  lastPage: '',
  firstPage: '',
  getNextPage: false,
  getPrevPage: false,
  totalElements: 0,
  completeValues: [],
};

function startLoading(state: MediaResistanceState, action: PayloadAction<boolean>) {
  if (!state.getNextPage && !state.getPrevPage && !action.payload) {
    state.isLoading = true;
    resetPage(state);
  }
}

function loadingFailed(state: MediaResistanceState, action: PayloadAction<string>) {
  state.isLoading = false;
  state.error = action.payload;
}

function fetchNext(state: MediaResistanceState) {
  state.getNextPage = true;
}

function fetchPrevious(state: MediaResistanceState) {
  state.getPrevPage = true;
}
function resetPage(state: MediaResistanceState) {
  state.value = [];
  state.nextPage = '';
  state.firstPage = '';
  state.lastPage = '';
  state.previousPage = '';
  state.getNextPage = false;
  state.getPrevPage = false;
}

function getCompleteMediaResistances(state: MediaResistanceState, action: PayloadAction<PageDTO>) {
  state.completeValues = action.payload.data;
}

export const mediaResistancesSlice = createSlice({
  name: 'mediaResistances',
  initialState,
  reducers: {
    getMediaResistancesStart: startLoading,
    getMediaResistancesSuccess(state, { payload }: PayloadAction<PageDTO>) {
      if (!state.getNextPage) {
        state.value = payload.data;
      } else {
        state.value = state.value.concat(payload.data);
      }
      state.isLoading = false;
      state.error = null;
      state.getNextPage = false;
      state.getPrevPage = false;
      state.nextPage = payload.next;
      state.firstPage = payload.first;
      state.previousPage = payload.previous;
      state.lastPage = payload.last;
      state.totalElements = payload.total;
    },
    getMediaResistancesFailure: loadingFailed,
    getCompleteMediaResistancesSuccess: getCompleteMediaResistances,
    fetchNextMediaResistances: fetchNext,
    fetchPreviousMediaResistances: fetchPrevious,
    resetMediaResistancePage: resetPage,
    resetCompleteValues: (state: MediaResistanceState) => {
      state.completeValues = [];
    },
  },
});

export const {
  getMediaResistancesStart,
  getMediaResistancesSuccess,
  getMediaResistancesFailure,
  fetchNextMediaResistances,
  fetchPreviousMediaResistances,
  resetMediaResistancePage,
  getCompleteMediaResistancesSuccess,
  resetCompleteValues,
} = mediaResistancesSlice.actions;

export default mediaResistancesSlice.reducer;

export const fetchMediaResistances =
  (query: string, getComplete: boolean, url?: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMediaResistancesStart(getComplete));
      const mediaResistance = await getMediaResistances(query, url);
      if (!getComplete) {
        dispatch(getMediaResistancesSuccess(mediaResistance));
      } else {
        dispatch(getCompleteMediaResistancesSuccess(mediaResistance));
      }
    } catch (err) {
      if (err instanceof Error) {
        dispatch(getMediaResistancesFailure(err.message));
      } else {
        dispatch(getMediaResistancesFailure("Can't dispatch getMediaResistances"));
      }
    }
  };
